import { createContext, useContext } from 'react';
import type { NotificationContextState } from '@/shared/ui/notification/types';

/**
 * The notification context.
 *
 * `null` is the default rather than a no-op stub. A component that calls
 * `useNotification` outside the provider is a wiring bug, and a silent no-op
 * would hide it — the notification would simply never appear and the operator
 * would be told nothing. `useNotification` throws instead, which surfaces the
 * mistake at the first render rather than in production.
 */
export const NotificationContext = createContext<NotificationContextState | null>(null);

/**
 * Read the notification API.
 *
 * Throws when there is no provider above the caller. That is deliberate: a
 * missing provider means a notification the operator will never see, and a
 * thrown error names the cause instead of swallowing it.
 */
export function useNotification(): NotificationContextState {
  const value = useContext(NotificationContext);
  if (value === null) {
    throw new Error(
      'useNotification must be used within a NotificationProvider. Wrap the application root.',
    );
  }
  return value;
}
